"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import statue from "@/public/motions/trading-competition/statue-1st.png";
import trophy from "@/public/motions/trading-competition/hero-trophy.png";
import texture from "@/public/motions/trading-competition/rules-card-1.jpg";

/**
 * Post-hero banner that sends the homepage to the Trading Competition page
 * (Figma "Banner pós-hero · Trading Competition": desktop 11172:1729 / DE 11279:528,
 * mobile 11279:1621 / DE 11279:559). Desktop is the 1364×140 strip, mobile the 398×373 card.
 *
 * Copy is per language. There is no BR competition page, so BR falls back to English.
 * The mobile frames use the desktop copy. The EN mobile frame says "Competiton", "200.000$" and
 * "No extra cos", and the DE mobile frame drops the period after "Competition".
 *
 * The frames are not laid out alike. The DE text block sits at 223/33 instead of EN's 188/40, the DE
 * highlight is spaced like the space characters around it rather than EN's 10px gaps, and the mobile
 * highlights differ in size. `block` and `highlight` carry those offsets for each language.
 */
const COPY = {
  en: {
    href: "/trading-competition",
    lead: "BIGGEST Trading Competition.",
    pre: "Win",
    amount: "$200,000",
    post: "on top.",
    subtitle: ["No sign-up · No extra cost ·", "Payouts + Prizes"],
    cta: "View the leaderboard",
    block: "top-[2.875rem] lg:top-[5rem] lg:left-[23.5rem]",
    line2: "gap-[0.25rem]",
    highlight: "h-[4rem] px-[0.5rem] lg:mx-[1.25rem]",
  },
  de: {
    href: "/de/trading-competition",
    lead: "GRÖßTE Trading Competition.",
    pre: "",
    amount: "200.000$",
    post: "gewinnen.",
    subtitle: ["Keine Anmeldung · Keine Zusatzkosten ·", "Auszahlungen + Preise"],
    cta: "Leaderboard ansehen",
    block: "top-[3.25rem] lg:top-[4.125rem] lg:left-[27.75rem]",
    // Figma centres the glyphs, not the highlight box, so the mobile line shifts left by half its right pad
    line2: "mr-[0.875rem] gap-[0.375rem]",
    highlight:
      "mb-[0.25rem] h-[3.75rem] pr-[0.875rem] pl-[0.75rem] lg:mr-[0.5rem] lg:mb-0 lg:ml-[0.375rem] lg:h-[4.25rem] lg:pr-[0.875rem] lg:pl-[0.625rem]",
  },
} as const;

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function TradingCompetitionBanner({ language }: { language?: string }) {
  const copy = stegaClean(language) === "de" ? COPY.de : COPY.en;

  return (
    // Desktop spacing follows the Figma frame: ~121px from the trust logos, ~139px to the pricing chip
    // (8rem here plus the pricing section's own 9.5rem). The top margins differ per breakpoint to even
    // out the hero's bottom margin. The gap above still drifts about ±16px inside each breakpoint
    // because the Trustpilot widget in the trust-logo row is sized in px. Mobile tucks into the hero's
    // bottom margin, as the pricing section did before the banner, and sits 64px from both neighbours.
    // The z-index keeps it above the hero's blur fade.
    <section className="relative z-[30] -mt-[5.5rem] mb-[8rem] w-full lg:mt-[11rem] lg:mb-[8rem] xl:mt-[14.25rem] 2xl:mt-[9.5rem]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease }}
        // Global `main { text-wrap: balance }` would re-wrap the Figma lines, and the global
        // `* { -webkit-font-smoothing: subpixel-antialiased }` renders white-on-green text heavier than Figma.
        className="relative mx-auto h-[46.625rem] w-[calc(100%-4rem)] overflow-clip rounded-[0.5rem] border border-solid border-white/10 bg-[#04271d] [text-wrap:wrap] [&_*]:antialiased lg:h-[17.5rem] lg:w-[min(170.5rem,100%)]">
        {/* Same 1482×1082.5 texture scale on both frames, centred, top at -478.25 */}
        <Image
          src={texture}
          alt=""
          aria-hidden="true"
          sizes="(min-width: 993px) 78vw, 345vw"
          className="pointer-events-none absolute top-[-59.906rem] left-1/2 h-[135.3125rem] w-[185.25rem] max-w-none -translate-x-1/2 opacity-[0.18] mix-blend-soft-light"
        />

        {/* Statue: desktop on the left edge, mobile left of the trophy above the CTA */}
        <div className="pointer-events-none absolute top-[19.25rem] left-[calc(50%-17.051rem)] h-[19.25rem] w-[23.714rem] overflow-hidden lg:top-[-0.25rem] lg:left-[2.125rem] lg:h-[25.5rem] lg:w-[21.25rem]">
          <Image
            src={statue}
            alt=""
            aria-hidden="true"
            sizes="(min-width: 993px) 9vw, 47vw"
            quality={90}
            className="absolute top-[-2.91%] left-0 h-[163.22%] w-full max-w-none lg:top-[-1.97%] lg:h-[110.42%]"
          />
        </div>

        <div className="pointer-events-none absolute top-[21.745rem] left-[calc(50%+7.784rem)] h-[16.755rem] w-[13.7185rem] -translate-x-1/2 overflow-hidden lg:top-[1.25rem] lg:left-[117.5rem] lg:h-[17.5rem] lg:w-[13.783rem] lg:translate-x-0">
          <Image
            src={trophy}
            alt=""
            aria-hidden="true"
            sizes="(min-width: 993px) 6vw, 28vw"
            quality={90}
            className="absolute top-[0.05%] left-[0.01%] h-[143.71%] w-[100.22%] max-w-none lg:top-[0.04%] lg:left-0 lg:h-[137.93%] lg:w-full"
          />
        </div>

        <div
          className={cn(
            "absolute left-1/2 flex w-[45.375rem] -translate-x-1/2 flex-col items-center gap-[1rem] text-center lg:w-auto lg:translate-x-0 lg:items-start lg:text-left",
            copy.block,
          )}>
          <p className="mb-0 flex flex-col items-center font-geist text-[3.25rem] leading-[1.3] font-medium tracking-[-0.0975rem] whitespace-nowrap text-white lg:flex-row lg:text-[3.75rem] lg:tracking-[-0.1125rem]">
            <span>
              {copy.lead}
              {copy.pre && <span className="max-lg:hidden"> {copy.pre}</span>}
              {/* Figma centres the mobile first line with its trailing space ("…Competition. ⏎") */}
              {copy.pre && <span className="lg:hidden">{" "}</span>}
            </span>
            <span className={cn("flex items-center lg:contents", copy.line2)}>
              {copy.pre && <span className="lg:hidden">{copy.pre}</span>}
              <span
                className={cn(
                  "flex h-[4rem] items-center bg-[#083628] px-[0.625rem] lg:h-auto lg:px-[0.75rem]",
                  copy.highlight,
                )}>
                <span className="font-blacker text-[3.25rem] leading-[1.1] font-normal tracking-normal text-[#d6eee7] italic lg:text-[4.25rem]">
                  {copy.amount}
                </span>
              </span>
              <span>{copy.post}</span>
            </span>
          </p>
          <p className="mb-0 pt-[0.125rem] font-geist text-[2rem] leading-[1.3] font-normal text-white/[0.72] lg:whitespace-nowrap">
            {copy.subtitle[0]}
            <br className="lg:hidden" /> {copy.subtitle[1]}
          </p>
        </div>

        <Link
          href={copy.href}
          className="group absolute right-[1.875rem] bottom-[1.875rem] left-[1.875rem] flex h-[6rem] items-center justify-center rounded-[0.25rem] bg-white lg:top-[6rem] lg:pr-[0.75rem] lg:pl-[3rem] lg:right-auto lg:bottom-auto lg:left-[135.625rem] lg:justify-start lg:gap-[2rem]">
          <span className="font-geist text-[2rem] leading-[1.3] font-semibold whitespace-nowrap text-[#003828]">
            {copy.cta}
          </span>
          <span className="absolute top-1/2 right-[0.75rem] flex h-[4.5rem] w-[4.5rem] -translate-y-1/2 items-center justify-center overflow-clip rounded-[0.25rem] bg-[#003828] lg:static lg:translate-y-0">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="h-[2rem] w-[2rem] transition-transform duration-300 group-hover:translate-x-[0.25rem]">
              <path
                d="M9 3L8.285 3.695L12.075 7.5H2V8.5H12.075L8.285 12.285L9 13L14 8L9 3Z"
                fill="white"
              />
            </svg>
          </span>
        </Link>
      </motion.div>
    </section>
  );
}
